import { formatDate, formatTime } from '@/utils/date-utils'
import { useState, useEffect } from 'react'
// Replace next/dynamic with a dynamic import approach that works with Vite
// Since we're using Vite, not Next.js

// Define types for the props
interface MeetupSchedulePdfProps {
  meetup: {
    title: string
    description: string | null
    location: string | null
    address: string | null
    start_time: string
    end_time: string
  }
  events: Array<{
    id: string
    title: string
    start_time: string
    end_time: string
    max_players?: number | null
    participant_count?: Array<{ count: number }> | null
    participants?: Array<{
      id: string
      profile_id: string
      full_name: string | null
      username: string | null
      status: string
      is_waitlisted: boolean
      waitlist_position: number | null
      avatar_url?: string | null
    }>
  }>
}

// Client-side only component wrapper
const ClientOnly = ({ children }: { children: React.ReactNode }) => {
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  if (!isClient) return null
  return <>{children}</>
}

// PDF component - client-side only
const MeetupSchedulePdf = ({ meetup, events }: MeetupSchedulePdfProps) => {
  // We'll load all PDF components lazily on the client side
  const [pdfComponents, setPdfComponents] = useState<any>(null)

  useEffect(() => {
    // Import all PDF components on the client side
    import('@react-pdf/renderer').then(pdf => {
      setPdfComponents(pdf)
    })
  }, [])

  if (!pdfComponents) {
    return null
  }

  const { Document, Page, Text, View, StyleSheet } = pdfComponents

  // Create styles
  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    header: {
      marginBottom: 20,
      padding: 10,
      borderBottom: '1px solid #ccc',
    },
    title: {
      fontSize: 24,
      marginBottom: 10,
      fontWeight: 'bold',
    },
    subtitle: {
      fontSize: 14,
      marginBottom: 5,
      color: '#555',
    },
    dateRange: {
      fontSize: 12,
      marginBottom: 10,
      color: '#666',
    },
    location: {
      fontSize: 12,
      marginBottom: 5,
      color: '#666',
    },
    dayHeader: {
      fontSize: 18,
      marginTop: 15,
      marginBottom: 10,
      fontWeight: 'bold',
      backgroundColor: '#f5f5f5',
      padding: 8,
      borderRadius: 4,
    },
    eventCard: {
      marginBottom: 15,
      padding: 10,
      borderRadius: 4,
      border: '1px solid #eee',
    },
    eventTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      marginBottom: 5,
    },
    eventTime: {
      fontSize: 12,
      marginBottom: 5,
      color: '#666',
    },
    playerCount: {
      fontSize: 12,
      marginBottom: 5,
      color: '#666',
    },
    participantsHeader: {
      fontSize: 12,
      fontWeight: 'bold',
      marginTop: 8,
      marginBottom: 5,
    },
    participant: {
      fontSize: 10,
      marginBottom: 3,
    },
    waitlist: {
      color: '#f59e0b',
    },
    normal: {
      color: '#10b981',
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
      right: 30,
      fontSize: 10,
      textAlign: 'center',
      color: '#999',
    },
  })

  const groupedEvents = groupEventsByDay(events)
  const days = Object.keys(groupedEvents).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  )

  return (
    <Document>
      {days.map((day, i) => (
        <Page key={i} size="A4" style={styles.page}>
          {/* Header at the top of each page */}
          <View style={styles.header}>
            <Text style={styles.title}>{meetup.title}</Text>
            {meetup.description && <Text style={styles.subtitle}>{meetup.description}</Text>}
            <Text style={styles.dateRange}>
              {formatDate(meetup.start_time)} - {formatDate(meetup.end_time)}
            </Text>
            {meetup.location && <Text style={styles.location}>Location: {meetup.location}</Text>}
            {meetup.address && <Text style={styles.location}>Address: {meetup.address}</Text>}
          </View>

          {/* Day header */}
          <Text style={styles.dayHeader}>{day}</Text>

          {/* Events for this day */}
          {groupedEvents[day].map(event => (
            <View key={event.id} style={styles.eventCard}>
              <Text style={styles.eventTitle}>{event.title}</Text>
              <Text style={styles.eventTime}>
                {formatTime(event.start_time)} - {formatTime(event.end_time)}
              </Text>

              <Text style={styles.playerCount}>
                Players: {event.participant_count?.[0]?.count || 0}
                {event.max_players ? ` / ${event.max_players}` : ''}
              </Text>

              {event.participants && event.participants.length > 0 && (
                <>
                  <Text style={styles.participantsHeader}>Participants:</Text>
                  {event.participants
                    .sort((a, b) => {
                      // Sort by waitlist status first, then by waitlist position
                      if (a.is_waitlisted !== b.is_waitlisted) {
                        return a.is_waitlisted ? 1 : -1 // Non-waitlisted first
                      }
                      if (a.is_waitlisted && b.is_waitlisted) {
                        return (a.waitlist_position || 0) - (b.waitlist_position || 0)
                      }
                      return 0
                    })
                    .map(participant => (
                      <Text
                        key={participant.id}
                        style={[
                          styles.participant,
                          participant.is_waitlisted ? styles.waitlist : styles.normal,
                        ]}
                      >
                        {participant.full_name || participant.username || 'Anonymous User'}
                        {participant.is_waitlisted
                          ? ` - Waitlist(${participant.waitlist_position})`
                          : " - I'm In"}
                      </Text>
                    ))}
                </>
              )}
            </View>
          ))}

          {/* Footer */}
          <Text style={styles.footer}>
            Schedule generated from Boardgames Today on {new Date().toLocaleDateString()}
          </Text>
        </Page>
      ))}
    </Document>
  )
}

// Group events by day function
const groupEventsByDay = (events: MeetupSchedulePdfProps['events']) => {
  const grouped: Record<string, MeetupSchedulePdfProps['events']> = {}

  events.forEach(event => {
    const date = formatDate(event.start_time)
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(event)
  })

  // Sort events by start time within each day
  Object.keys(grouped).forEach(date => {
    grouped[date].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    )
  })

  return grouped
}

// Export the PDF download link wrapper component with client-only rendering
export const MeetupSchedulePdfDownload = ({
  meetup,
  events,
  fileName = 'meetup-schedule.pdf',
}: MeetupSchedulePdfProps & { fileName?: string }) => {
  const [pdfComponents, setPdfComponents] = useState<any>(null)

  useEffect(() => {
    // Import PDF components on the client side
    import('@react-pdf/renderer').then(pdf => {
      setPdfComponents(pdf)
    })
  }, [])

  if (!pdfComponents) {
    return (
      <button
        className="bg-primary text-white py-2 px-4 rounded-md font-medium focus:outline-none w-full text-center h-10"
        disabled
      >
        Export Events
      </button>
    )
  }

  const { PDFDownloadLink } = pdfComponents

  return (
    <PDFDownloadLink
      document={<MeetupSchedulePdf meetup={meetup} events={events} />}
      fileName={fileName}
      className="bg-primary text-white py-2 px-4 rounded-md font-medium hover:bg-primary/90 focus:outline-none w-full text-center h-10 flex items-center justify-center"
    >
      {({ loading }: { loading: boolean }) => (loading ? 'Generating PDF...' : 'Export Events')}
    </PDFDownloadLink>
  )
}

export default MeetupSchedulePdfDownload
